import {
  loadLatestFields,
  prepareStandardOutputFields,
} from './output-layout-service';
import { generateId } from '@/app/core';
import {
  EmptyResponse,
  InputFileInformation,
  InputLayoutField,
  OutputLayoutField,
  SSARProgram,
} from '@ssar-app/domain';
import { Alert, AlertProps, Modal, Spin } from '@ssar-app/uikit';
import { difference, differenceBy, isEqual, pick } from 'lodash';
import { ReactNode } from 'react';

export const isChanged = (
  diffs: {
    added?: Partial<SSARProgram>;
    removed?: Partial<SSARProgram>;
  },
  key: 'added' | 'removed',
) => {
  const json = diffs[key] || {};
  return json.attributesAndBundles?.attributes?.length ||
    json.attributesAndBundles?.bundles?.length ||
    Object.keys(json.demographics || {}).length ||
    extractInputFieldsFromProgram((json || {}) as SSARProgram).length ||
    json.scoresAndAddonProducts?.scores?.length
    ? true
    : false;
};

export const getDiff = (
  prev?: SSARProgram,
  present?: SSARProgram,
): {
  added?: Partial<SSARProgram>;
  removed?: Partial<SSARProgram>;
  hasChanged?: boolean;
} => {
  const demographics = { added: {}, removed: {} };
  Object.keys(present?.demographics || {}).filter((d) => {
    if (
      !(prev?.demographics as Record<string, boolean>)?.[d] &&
      (present?.demographics as Record<string, boolean>)?.[d]
    ) {
      demographics['added'] = { ...demographics['added'], [d]: true };
    } else if (
      !(present?.demographics as Record<string, boolean>)?.[d] &&
      (prev?.demographics as Record<string, boolean>)?.[d]
    ) {
      demographics['removed'] = { ...demographics['removed'], [d]: true };
    }
  });
  const diffs = {
    added: {
      programKey: present?.programKey || '',
      subCode: present?.subCode || '',
      scoresAndAddonProducts: {
        scores: difference(
          present?.scoresAndAddonProducts?.scores || [],
          prev?.scoresAndAddonProducts?.scores || [],
        ),
      },
      demographics: demographics.added,
      attributesAndBundles: {
        attributes: differenceBy(
          present?.attributesAndBundles?.attributes || [],
          prev?.attributesAndBundles?.attributes || [],
          'code',
        ),
        bundles: difference(
          present?.attributesAndBundles?.bundles || [],
          prev?.attributesAndBundles?.bundles || [],
        ),
      },
      reports: { distribution: { attributes: [] } },
      inputFile: {
        ...(present?.inputFile as InputFileInformation),
        fieldsInfo: {
          source: present?.inputFile?.fieldsInfo?.source || 'Inline',
          fields: getDifferenceInFields(
            present?.inputFile?.fieldsInfo?.fields || [],
            prev?.inputFile?.fieldsInfo?.fields || [],
          ),
        },
      },
    },
    removed: {
      programKey: present?.programKey || '',
      subCode: present?.subCode || '',
      scoresAndAddonProducts: {
        scores: difference(
          prev?.scoresAndAddonProducts?.scores || [],
          present?.scoresAndAddonProducts?.scores || [],
        ),
      },
      demographics: demographics.removed,
      attributesAndBundles: {
        attributes: differenceBy(
          prev?.attributesAndBundles?.attributes || [],
          present?.attributesAndBundles?.attributes || [],
          'code',
        ),
        bundles: difference(
          prev?.attributesAndBundles?.bundles || [],
          present?.attributesAndBundles?.bundles || [],
        ),
      },
      reports: { distribution: { attributes: [] } },
      inputFile: {
        ...(present?.inputFile as InputFileInformation),
        fieldsInfo: {
          source: present?.inputFile?.fieldsInfo?.source || 'Inline',
          fields: getDifferenceInFields(
            prev?.inputFile?.fieldsInfo?.fields || [],
            present?.inputFile?.fieldsInfo?.fields || [],
          ),
        },
      },
    },
  };
  const hasChanged = isChanged(diffs, 'added') || isChanged(diffs, 'removed');
  return { ...diffs, hasChanged: hasChanged };
};

export const getDifferenceInFields = (
  presentFields: InputLayoutField[],
  prevFields: InputLayoutField[],
) => {
  if (presentFields.length !== prevFields.length) {
    return presentFields;
  }
  const areSameHeaders = isEqual(
    presentFields.map((field) => {
      const pickedField = pick(field, ['name', 'headerName', 'fieldLength']);
      return { ...pickedField, name: pickedField.name || undefined };
    }),
    prevFields.map((field) => {
      const pickedField = pick(field, ['name', 'headerName', 'fieldLength']);
      return { ...pickedField, name: pickedField.name || undefined };
    }),
  );
  if (!areSameHeaders) {
    return presentFields;
  }
  return [];
};

export const extractInputFieldsFromProgram = (
  program?: Partial<SSARProgram>,
) => {
  return program?.inputFile?.fieldsInfo?.fields || [];
};

export const updateAndSortFields = (newRows: OutputLayoutField[]) => {
  let start = 1;
  return newRows
    .map((field, index) => {
      const toReturn = {
        ...field,
        meta: {
          id: generateId(),
          ...field.meta,
          index: index + 1,
          start: start,
          end: start + Number(field.fieldLength) - 1,
        },
      };
      start = start + Number(field.fieldLength);
      return toReturn;
    })
    .filter((row) => !!row);
};

export const checkAndGetLatestOutputFields = async (
  presentData: SSARProgram,
  prevData: SSARProgram,
) => {
  const isCustomized = presentData?.outputLayout?.isCustomized || false;
  const modal = Modal.default({
    content: (
      <Spin
        containerProps={{ py: 16 }}
        spinning
        tip='Loading Output fields...'
      />
    ),
    closeOnEsc: false,
    closeOnOverlayClick: false,
    showCloseButton: false,
    showOk: false,
    visible: false,
  });
  const differences = getDiff(prevData, presentData);
  const loadStandardOutputFields = () => {
    return prepareStandardOutputFields(presentData, false)
      .then((resetProgram) => {
        return {
          ...presentData.outputLayout,
          isCustomized: false,
          fields: resetProgram.outputLayout.fields,
        };
      })
      .catch((error) => {
        throw new Error(error);
      })
      .finally(() => modal.destroy());
  };
  if (differences.hasChanged) {
    modal.update({ visible: true });
    if (isCustomized) {
      if (
        extractInputFieldsFromProgram(differences.added).length !== 0 ||
        extractInputFieldsFromProgram(differences.removed).length !== 0
      ) {
        return loadStandardOutputFields();
      }
      return loadLatestFields(
        differences.added as SSARProgram,
        differences.removed as SSARProgram,
        presentData,
      )
        .then((outputProgram) => {
          return {
            ...presentData.outputLayout,
            fields: outputProgram.outputLayout.fields,
          };
        })
        .catch((error) => {
          throw new Error(error);
        })
        .finally(() => modal.destroy());
    } else {
      return loadStandardOutputFields();
    }
  } else {
    return {
      ...presentData.outputLayout,
      fields: presentData.outputLayout.fields,
    };
  }
};

export const checkDuplicates = (
  finalOutputFields: OutputLayoutField[],
): { containsDups: boolean; duplicates: Record<string, number[]> } => {
  const duplicates: EmptyResponse = {};
  let containsDups = false;
  finalOutputFields.map((field, index) => {
    const headerName = field.headerName?.toUpperCase() || '';
    if (duplicates[headerName]?.length) {
      duplicates[headerName].push(index + 1);
      containsDups = true;
    } else {
      duplicates[headerName] = [index + 1];
    }
  });
  return { containsDups: containsDups, duplicates: duplicates };
};

export interface AlertMessageProps {
  message?: ReactNode;
  type: AlertProps['type'];
  isOpen?: boolean;
}

export const AlertMessage = (props: AlertMessageProps) => {
  return (
    <>
      {props.isOpen ? (
        <Alert
          titleProps={{ fontWeight: 'medium' }}
          description={props.message}
          type={props.type}
          isOpen
          showCloseButton={false}
          width='full'
          p={2}
          variant='left-accent'
          background={'orange.200'}
        />
      ) : (
        ''
      )}
    </>
  );
};
